import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import UserContext from '../../../../components/User_Context.js';
import * as api from "../../../../api/apiConnector.js";
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import useFormLock from './useFormLock.js';

/**
 * Custom hook for managing form data state and operations
 * Handles both simulation mode (for form testing) and compilation mode (for actual form filling)
 * 
 * @param {boolean} isSimulation - Whether the form is in simulation mode
 * @param {object} propForm - Form object passed as prop (used in simulation mode)
 * @param {function} onClose - Callback function to close the form
 * @param {object} colors - Color scheme for UI components
 * @param {object} propFormCompletion - Form completion data passed as prop (optional)
 * @returns {object} Object containing all state variables and functions needed for form operations
 */
const useFormData = (isSimulation, propForm, onClose, colors, propFormCompletion = null) => {
  // Router hooks for navigation and URL parameters
  const { formId, completionId } = useParams();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignmentId');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);
  const { currentUser } = useContext(UserContext);

  // Core state variables for form data management
  const [loading, setLoading] = useState(isSimulation ? false : true);
  const [form, setForm] = useState(isSimulation ? propForm : null);
  const [formCompletion, setFormCompletion] = useState(propFormCompletion);
  const [collaborativeFormsSchema, setCollaborativeFormsSchema] = useState([]);
  const [workflowInfo, setWorkflowInfo] = useState(null);
  const [formAssignmentId, setFormAssignmentId] = useState(null);
  const [collaborativeAssignmentId, setCollaborativeAssignmentId] = useState(null);

  // Form values and user inputs
  const [formValues, setFormValues] = useState({});
  const [answerFiles, setAnswerFiles] = useState({});
  const [answerLinks, setAnswerLinks] = useState({});
  const [answerObservations, setAnswerObservations] = useState({});
  const [answerTexts, setAnswerTexts] = useState({});
  const [activeAttachmentTab, setActiveAttachmentTab] = useState({});

  // System-related state
  const [answersFolderId, setAnswersFolderId] = useState(null);
  const [preventQuit, setPreventQuit] = useState(false);

  // Vocabulary and organizational data
  const [vocabularyItems, setVocabularyItems] = useState({});
  const [customVocabularyItems, setCustomVocabularyItems] = useState({});
  const [departmentsVoc, setDepartmentsVoc] = useState([]);
  const [functionalAreaVocs, setFunctionalAreaVocs] = useState([]);

  // Lock state for collaborative forms
  const [isCollaborative, setIsCollaborative] = useState(false);

  // Use lock hook for collaborative forms (only in compilation mode with completionId)
  const lockState = useFormLock(
    !isSimulation && completionId ? completionId : null,
    currentUser(),
    isCollaborative
  );

  /**
   * Sets up breadcrumb navigation for compilation mode
   * Skipped in simulation mode as it's displayed in a modal
   */
  const setBreadCrumb = () => {
    if (isSimulation) return;

    const formName = form?.Name || t('form', { ns: 'labels', defaultValue: 'Form' });

    const path = [
      { link: "/", name: t('home', { ns: 'labels', defaultValue: 'Home' }) },
      { link: `/form_builder/${formId}`, name: formName },
      { link: `/form/compile/${formId}/${completionId || ''}`, name: t('formCompilation', { ns: 'labels', defaultValue: 'Form Compilation' }) }
    ];
    breadcrumb.set(path);
  };

  /**
   * Retrieves the answers folder ID from the backend
   * This folder is used to store file attachments for form answers
   */
  const getAnswersFolderId = async () => {
    try {
      const result = await api.sendMessage("dms/getAnswersFolderId", {
        user: currentUser(),
        body: {}
      });

      if (result?.success && result?.folderId) {
        setAnswersFolderId(result.folderId);
      } else {
        console.error("Failed to get answers folder ID:", result);
        MySwal.fire({
          title: t('error', { ns: 'labels' }),
          text: t('failedToRetrieveFiles', { ns: 'labels' }),
          icon: 'error',
          confirmButtonColor: colors?.danger || '#dc3545'
        });
      }
    } catch (error) {
      console.error("Error getting answers folder ID:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });
    }
  };

  /**
   * Loads form data and completion information from the backend
   * Only used in compilation mode - simulation mode uses props directly
   */
  const loadFormData = async () => {
    if (isSimulation) return;

    // Use provided form completion data for read-only mode
    if (propFormCompletion) {
      setForm(propFormCompletion.form);
      setFormCompletion(propFormCompletion);

      // Initialize form values from provided completion data
      initializeFormValues(propFormCompletion);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const result = await api.sendMessage("darm/formCompletion/get", {
        user: currentUser(),
        body: {
          formId: formId,
          completionId: completionId,
          assignmentId: assignmentId
        }
      });


      if (result?.success) {
        setForm(result.form);
        setFormCompletion(result.formCompletion);

        // Check if form is collaborative
        if (result.form && result.form.IsCollaborative) {
          setIsCollaborative(true);
        }

        // Save collaborative forms schema if present
        // Schema structure: [{ actor: {id, name}, questions: [questionIds], sections: [sectionIds], answers: {questionId: [answerIds]} }]
        // Questions array includes both directly assigned questions and questions from assigned sections
        if (result.collaborativeFormsSchema && Array.isArray(result.collaborativeFormsSchema)) {
          // Debug log to verify schema structure
          console.log('Collaborative Schema loaded (RAW from backend):', result.collaborativeFormsSchema);

          // Ensure each actor has an answers object (fallback for backend compatibility)
          const normalizedSchema = result.collaborativeFormsSchema.map(actor => ({
            ...actor,
            answers: actor.answers || {} // Initialize empty answers object if missing
          }));

          console.log('Collaborative Schema loaded (NORMALIZED):', normalizedSchema);
          setCollaborativeFormsSchema(normalizedSchema);
        } else {
          setCollaborativeFormsSchema([]);
        }

        // Save workflow information if present
        if (result.workflowInfo) {
          setWorkflowInfo(result.workflowInfo);
        } else {
          setWorkflowInfo(null);
        }

        // Save assignment IDs if present (at same level as workflowInfo)
        if (result.formAssignmentId) {
          setFormAssignmentId(result.formAssignmentId);
        } else {
          setFormAssignmentId(null);
        }

        if (result.collaborativeAssignmentId) {
          setCollaborativeAssignmentId(result.collaborativeAssignmentId);
        } else {
          setCollaborativeAssignmentId(null);
        }

        // Initialize form values from existing completion data
        initializeFormValues(result.formCompletion);

        setBreadCrumb();
      } else {
        MySwal.fire({
          title: t('error', { ns: 'labels' }),
          text: result?.error || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form' }),
          icon: 'error',
          confirmButtonColor: colors?.danger || '#dc3545'
        });

        if (onClose) {
          onClose();
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error("Error loading form data:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form' }),
        icon: 'error',
        confirmButtonColor: colors?.danger || '#dc3545'
      });

      if (onClose) {
        onClose();
      } else {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initializes form values, files, and attachments from completion data
   * Processes the standardized answer structure from the backend.
   * Supports both shapes: formCompletion.data.answers (save structure) and formCompletion.answers (top-level).
   *
   * @param {object} formCompletionData - The form completion data from backend
   */
  const initializeFormValues = (formCompletionData) => {
    if (!formCompletionData) return;

    const initialValues = {};
    const initialFiles = {};
    const initialLinks = {};
    const initialObservations = {};
    const initialTexts = {};

    // Track multiple choice answers separately as they need to be grouped
    const multipleAnswersMap = {};

    // Support both formCompletion.data.answers and formCompletion.answers (backend may return either)
    const completionData = formCompletionData.data || formCompletionData;
    const answers = Array.isArray(completionData?.answers)
      ? completionData.answers
      : Array.isArray(formCompletionData.answers)
        ? formCompletionData.answers
        : [];

      if (Array.isArray(answers)) {
        answers.forEach(answer => {
          if (answer.questionId) {
            // Initialize attachments using standardized structure (check for null explicitly)
            if (answer.file !== null && answer.file !== undefined) {
              initialFiles[answer.questionId] = {
                Id: answer.file,
                Name: answer.fileName || 'File',
                MimeType: answer.fileMimeType || 'application/octet-stream',
                Size: answer.fileSize || 0
              };
            }

            if (answer.link !== null && answer.link !== undefined) {
              initialLinks[answer.questionId] = answer.link;
            }

            if (answer.observation !== null && answer.observation !== undefined) {
              initialObservations[answer.questionId] = answer.observation;
            }

            if (answer.text !== null && answer.text !== undefined) {
              initialTexts[answer.questionId] = answer.text;
            }

            // Handle different question types using standardized structure
            if (answer.questionType === 'multiple') {
              // GROUP 5 - MULTIPLE CHOICE: Collect answer IDs in an array
              if (!multipleAnswersMap[answer.questionId]) {
                multipleAnswersMap[answer.questionId] = [];
              }
              if (Array.isArray(answer.answers)) {
                answer.answers.forEach(ans => {
                  if (ans.answerId) {
                    multipleAnswersMap[answer.questionId].push(ans.answerId);
                  }
                });
              }
            } else if (answer.questionType === 'pdca') {
              // PDCA - MULTIPLE CHOICE: Collect answer IDs in an array
              if (!multipleAnswersMap[answer.questionId]) {
                multipleAnswersMap[answer.questionId] = [];
              }
              if (Array.isArray(answer.answers)) {
                answer.answers.forEach(ans => {
                  if (ans.answerId) {
                    multipleAnswersMap[answer.questionId].push(ans.answerId);
                  }
                });
              }
            } else if (['single', 'boolean', 'yesNoNA'].includes(answer.questionType)) {
              // GROUP 1 - SINGLE CHOICE: Use the first answer ID
              if (Array.isArray(answer.answers) && answer.answers.length > 0) {
                initialValues[answer.questionId] = answer.answers[0].answerId;
              }
            } else if (answer.questionType === 'vocabulary') {
              // GROUP 3 - VOCABULARY: Support multiple selection by collecting all answer IDs
              if (!multipleAnswersMap[answer.questionId]) {
                multipleAnswersMap[answer.questionId] = [];
              }
              if (Array.isArray(answer.answers)) {
                answer.answers.forEach(ans => {
                  if (ans.answerId) {
                    multipleAnswersMap[answer.questionId].push(ans.answerId);
                  }
                });
              }
            } else if (answer.questionType === 'customVocabulary') {
              // GROUP 3 - CUSTOM VOCABULARY: Support multiple selection by collecting all vocabulary codes
              if (!multipleAnswersMap[answer.questionId]) {
                multipleAnswersMap[answer.questionId] = [];
              }
              if (Array.isArray(answer.answers)) {
                answer.answers.forEach(ans => {
                  let vocCode = null;

                  // Use the text value (vocValueText) as code since customVocabulary uses Value as Code
                  if (ans.vocValueText) {
                    // Use vocValueText as the code (new structure) 
                    vocCode = ans.vocValueText;
                  } else if (ans.answerId) {
                    // Fallback to old structure (answerId)
                    vocCode = ans.answerId;
                  }

                  if (vocCode && !multipleAnswersMap[answer.questionId].includes(vocCode)) {
                    multipleAnswersMap[answer.questionId].push(vocCode);
                  }
                });
              }
            } else if (['open', 'openNumber', 'date', 'time', 'datetime'].includes(answer.questionType)) {
              // GROUP 2 - FREE INPUT and GROUP 4 - DATE/TIME: Use answer text
              if (Array.isArray(answer.answers) && answer.answers.length > 0) {
                initialValues[answer.questionId] = answer.answers[0].answerText;
              }
            } else if (answer.questionType === 'departmentFunctionalArea') {
              // GROUP 6 - DEPARTMENT/FUNCTIONAL AREA: Parse combined value
              if (Array.isArray(answer.answers) && answer.answers.length > 0) {
                // Combined value format: "departmentId|functionalAreaId"
                initialValues[answer.questionId] = answer.answers[0].answerText;
              }
            } else if (answer.questionType === 'table') {
              // GROUP 7 - TABLE: Parse JSON data (answerText can be JSON string or already an array)
              if (Array.isArray(answer.answers) && answer.answers.length > 0) {
                const raw = answer.answers[0].answerText;
                try {
                  const tableData = typeof raw === 'string' ? JSON.parse(raw) : raw;
                  if (Array.isArray(tableData)) {
                    initialValues[answer.questionId] = tableData;
                  }
                } catch (error) {
                  console.error('Error parsing table data for question', answer.questionId, error);
                  initialValues[answer.questionId] = [];
                }
              }
            }
          }
        });

        // Apply grouped multiple choice answers (multiple, pdca, vocabulary, customVocabulary)
        Object.entries(multipleAnswersMap).forEach(([questionId, answerIds]) => {
          if (answerIds.length > 0) {
            // Always use arrays for multiple choice questions to maintain consistency
            // The UI components now expect arrays for vocabulary and customVocabulary questions
            initialValues[questionId] = answerIds;
          }
        });
      }

    // Update all state variables with initialized values
    setFormValues(initialValues);
    setAnswerFiles(initialFiles);
    setAnswerLinks(initialLinks);
    setAnswerObservations(initialObservations);
    setAnswerTexts(initialTexts);
  };

  /**
   * Retrieves vocabulary items from the backend for a specific vocabulary
   * Results are cached to avoid repeated API calls
   * 
   * @param {string} vocabularyId - The ID of the vocabulary to retrieve
   * @returns {Array} Array of vocabulary items
   */
  const getVocabularyItems = async (vocabularyId) => {
    // Return cached items if already loaded
    if (vocabularyItems[vocabularyId]) {
      return vocabularyItems[vocabularyId];
    }

    try {
      const result = await api.sendMessage("admin/vocs/get", {
        user: currentUser(),
        body: {
          voc_id: vocabularyId
        }
      });

      if (result?.items && Array.isArray(result.items)) {
        // Normalize vocabulary items structure
        const formattedItems = result.items.map(item => ({
          Code: item.Code || item.id,
          Name: item.Name || item.name,
          ...item
        }));

        // Cache the formatted items
        setVocabularyItems(prev => ({
          ...prev,
          [vocabularyId]: formattedItems
        }));

        return formattedItems;
      }

      console.error("Failed to get vocabulary items:", result);
      return [];
    } catch (error) {
      console.error("Error getting vocabulary items:", error);
      return [];
    }
  };

  /**
   * Retrieves custom vocabulary items from the backend for a specific custom vocabulary
   * Results are cached to avoid repeated API calls
   * 
   * @param {string} customVocabularyId - The ID of the custom vocabulary to retrieve
   * @returns {Array} Array of custom vocabulary items
   */
  const getCustomVocabularyItems = async (customVocabularyId) => {
    // Return cached items if already loaded
    if (customVocabularyItems[customVocabularyId]) {
      return customVocabularyItems[customVocabularyId];
    }

    try {
      const result = await api.sendMessage("admin/form_custom_vocs/get_values", {
        user: currentUser(),
        body: {
          vocId: customVocabularyId
        }
      });

      if (result?.success && result?.values && Array.isArray(result.values)) {
        // Normalize custom vocabulary items structure to match standard vocabulary format
        const formattedItems = result.values.map(item => ({
          Code: item.Value || item.Id,
          Name: item.Value || item.Name,
          ...item
        }));

        // Cache the formatted items
        setCustomVocabularyItems(prev => ({
          ...prev,
          [customVocabularyId]: formattedItems
        }));

        return formattedItems;
      }

      console.error("Failed to get custom vocabulary items:", result);
      return [];
    } catch (error) {
      console.error("Error getting custom vocabulary items:", error);
      return [];
    }
  };

  /**
   * Retrieves organizational units (departments and functional areas) from the backend
   * Used for department/functional area question types
   * 
   * @returns {object} Object containing departments and functional areas arrays
   */
  const getOrganizationalUnits = async () => {
    try {
      const result = await api.sendMessage("admin/system/organization/get_business_units", {
        user: currentUser()
      });

      if (result?.units && Array.isArray(result.units)) {
        // Separate departments and functional areas by unit type
        const departments = result.units.filter(x => x.UnitTypeCode === 'DEP');
        const functionalAreas = result.units.filter(x => x.UnitTypeCode === 'OPUNIT');

        setDepartmentsVoc(departments);
        setFunctionalAreaVocs(functionalAreas);

        return { departments, functionalAreas };
      }

      console.error("Failed to get organizational units:", result);
      return { departments: [], functionalAreas: [] };
    } catch (error) {
      console.error("Error getting organizational units:", error);
      return { departments: [], functionalAreas: [] };
    }
  };

  /**
   * Recursively finds all descendant functional areas for a given parent department
   * Used to populate functional area dropdown based on selected department
   * 
   * @param {Array} functionalAreaVocs - Array of all functional areas
   * @param {string} parentId - ID of the parent department
   * @returns {Array} Array of descendant functional areas
   */
  const getDescendants = (functionalAreaVocs, parentId) => {
    // Find direct children of the parent
    const directChildren = functionalAreaVocs.filter(x => x.ParentId === parentId);

    // Recursively find descendants and flatten the result
    const descendants = directChildren.flatMap(child =>
      [child, ...getDescendants(functionalAreaVocs, child.Id)]
    );

    return descendants;
  };

  // Initialize hook when component mounts or key parameters change
  useEffect(() => {
    (async () => {
      await getAnswersFolderId();
      await getOrganizationalUnits();

      // Load form data only in compilation mode
      if (!isSimulation) {
        await loadFormData();
      }
    })();

  }, [isSimulation, formId, completionId]);

  // Load vocabulary items when form data is available
  useEffect(() => {
    /**
     * Loads vocabulary items for all vocabulary-type questions in the form
     * This ensures vocabularies are available when questions are rendered
     */

    (async () => {
      const loadVocabularyItems = async () => {
        if (form?.sections) {
          for (const section of form.sections) {
            if (section.questions) {
              for (const question of section.questions) {
                if (question.questionType === 'vocabulary' && question.selectedVocabulary) {
                  await getVocabularyItems(question.selectedVocabulary);
                } else if (question.questionType === 'customVocabulary' && question.selectedCustomVocabulary) {
                  await getCustomVocabularyItems(question.selectedCustomVocabulary);
                } else if (question.questionType === 'table' && Array.isArray(question.tableColumns)) {
                  // Preload custom vocabulary items used by table columns
                  for (const col of question.tableColumns) {
                    if (col?.type === 'customVocabulary' && col.selectedCustomVocabulary) {
                      await getCustomVocabularyItems(col.selectedCustomVocabulary);
                    }
                  }
                }
              }
            }
          }
        }
      };

      if (form) {
        await loadVocabularyItems();
      }

    })();
  }, [form]);

  // Return all state variables and functions needed by components
  return {
    loading,
    form,
    formCompletion,
    setFormCompletion,
    formValues,
    setFormValues,
    answerFiles,
    setAnswerFiles,
    answerLinks,
    setAnswerLinks,
    answerObservations,
    setAnswerObservations,
    answerTexts,
    setAnswerTexts,
    activeAttachmentTab,
    setActiveAttachmentTab,
    answersFolderId,
    preventQuit,
    setPreventQuit,
    vocabularyItems,
    customVocabularyItems,
    departmentsVoc,
    functionalAreaVocs,
    getDescendants,
    loadFormData,
    MySwal,
    currentUser,
    collaborativeFormsSchema,
    workflowInfo,
    formAssignmentId,
    collaborativeAssignmentId,
    // Lock state for collaborative forms
    lockState,
    isCollaborative
  };
};

export default useFormData; 